import { ArrowRight, Camera, Check, CircleCheck, Lock, Mars, Pencil, Trash2, Venus } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import type { PhotoUpload, RegistrationForm } from '@escala/contracts';
import { BrandMark, pageTitle } from '@/components/Brand';
import { AddressFields, ChurchFields, FormField, fieldErrorProps } from '@/components/MemberForm';
import MinistryIcon from '@/components/MinistryIcon';
import OrgMark from '@/components/OrgMark';
import { PhotoDialog } from '@/components/PhotoDialog';
import { LegalLink } from '@/components/Privacy';
import { cn } from '@/design-system/lib/cn';
import { Card } from '@/design-system/patterns/Card';
import { Alert } from '@/design-system/ui/Alert';
import { Avatar } from '@/design-system/ui/Avatar';
import { Button } from '@/design-system/ui/Button';
import { CheckboxMark } from '@/design-system/ui/Checkbox';
import { DateField } from '@/design-system/ui/DateField';
import { Input } from '@/design-system/ui/Input';
import { SegmentedControl } from '@/design-system/ui/SegmentedControl';
import { SimpleSelect } from '@/design-system/ui/Select';
import { formatNumericDate, todayISO } from '@escala/domain/dates';
import {
  CHURCH_STATUS_HINT,
  CHURCH_STATUS_LABEL,
  CHURCH_STATUS_ORDER,
  CIVIL_STATUS_LABEL,
  JOINED_VIA_LABEL,
  addressLine,
  formatPhone,
  phoneDigits,
  showsOriginChurch,
} from '@escala/domain/members';
import { photoDataUrl } from '@escala/domain/photo';
import {
  CONSENT_TEXT,
  REGISTRATION_STEPS,
  emptyRegistrationData,
  normalizeRegistration,
  registrationStepErrors,
  type RegistrationErrors,
  type RegistrationStep,
} from '@escala/domain/registrations';
import type { CivilStatus, RegistrationData, Sex } from '@escala/domain/types';

import { authErrorMessage } from './authError';

/** De onde o formulário lê a igreja e para onde envia (a API pública; nos testes, uma fonte falsa). */
export interface RegistrationSource {
  load: () => Promise<RegistrationForm>;
  /** Os dados e a foto opcional (já recortada e reduzida no navegador). */
  submit: (data: RegistrationData, photo: PhotoUpload | null) => Promise<void>;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; form: RegistrationForm }
  | { kind: 'done'; form: RegistrationForm; firstName: string };

const CIVIL_OPTIONS = [
  { value: 'none', label: 'Prefiro não informar', pin: true },
  ...(Object.entries(CIVIL_STATUS_LABEL) as [CivilStatus, string][]).map(([value, label]) => ({
    value,
    label,
  })),
];

const SEX_LABEL: Record<Sex, string> = { F: 'Feminino', M: 'Masculino' };

const GRID = 'grid grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))] gap-4';

/**
 * Cadastro público pelo link da igreja (`/{igreja}/cadastro/{token}`), em 4 etapas — seus
 * dados (com a foto opcional, que ajuda a secretaria a reconhecer a pessoa), endereço, vida
 * na igreja e revisão (com a autorização da LGPD). O envio entra como solicitação para o
 * Administrador aprovar. Com o link pausado, "Cadastros encerrados".
 */
export default function PublicRegistration({
  source,
  closeLabel = 'Voltar',
  onClose,
}: {
  source: RegistrationSource;
  closeLabel?: string;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [today] = useState(todayISO);
  // Quem rola é esta camada (fixa, com rolagem própria), não a janela.
  const scroller = useRef<HTMLDivElement>(null);
  const toTop = () => {
    if (scroller.current) scroller.current.scrollTop = 0;
  };

  useEffect(() => {
    document.title = pageTitle('Cadastro de membro');

    let active = true;

    source
      .load()
      .then((form) => active && setPhase({ kind: 'ready', form }))
      .catch((error: unknown) => {
        if (!active) return;
        setPhase(
          error && typeof error === 'object' && 'status' in error && error.status === 404
            ? { kind: 'missing' }
            : { kind: 'failed' }
        );
      });

    return () => {
      active = false;
    };
  }, [source]);

  const form = phase.kind === 'ready' || phase.kind === 'done' ? phase.form : null;

  return (
    <div
      ref={scroller}
      role="dialog"
      aria-modal="true"
      aria-label="Cadastro de membro"
      className="fixed inset-0 z-50 overflow-y-auto bg-page text-ink"
    >
      <div className="mx-auto flex max-w-170 flex-col gap-5 px-4 pt-7 pb-12">
        <header className="flex items-center gap-2.5">
          <BrandMark />
          <span className="flex-1" />
          {form ? (
            <span className="flex h-9.5 min-w-0 items-center gap-2 rounded-control border border-line-input bg-white pr-3 pl-1.5">
              <OrgMark org={form.org} size="sm" />
              <span className="max-w-55 truncate text-small font-semibold max-sm:max-w-30">
                {form.org.name}
              </span>
            </span>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            {closeLabel}
          </Button>
        </header>

        {phase.kind === 'loading' ? (
          <Card className="p-10 text-center text-ink-subtle" role="status">
            Abrindo o formulário…
          </Card>
        ) : null}
        {phase.kind === 'missing' ? (
          <Notice
            icon={<Lock className="size-6 stroke-[1.8]" />}
            tone="warning"
            title="Link de cadastro inválido"
          >
            Este link não existe mais — talvez a igreja tenha gerado um novo. Peça o link atualizado à
            secretaria.
          </Notice>
        ) : null}
        {phase.kind === 'failed' ? (
          <Notice
            icon={<Lock className="size-6 stroke-[1.8]" />}
            tone="warning"
            title="Não foi possível abrir"
          >
            Confira a internet e tente de novo em instantes.
          </Notice>
        ) : null}
        {phase.kind === 'ready' && !phase.form.open ? (
          <Notice
            icon={<Lock className="size-6 stroke-[1.8]" />}
            tone="warning"
            title="Cadastros encerrados no momento"
          >
            Este link não está recebendo novos cadastros. Fale com a secretaria da igreja para receber um link
            atualizado.
          </Notice>
        ) : null}
        {phase.kind === 'done' ? (
          <Notice
            icon={<CircleCheck className="size-7 stroke-[1.8]" />}
            tone="success"
            title={`Cadastro enviado, ${phase.firstName}!`}
            actions={
              <>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => {
                    setPhase({ kind: 'ready', form: phase.form });
                    toTop();
                  }}
                >
                  Cadastrar outra pessoa
                </Button>
                <Button size="lg" onClick={onClose}>
                  {closeLabel}
                </Button>
              </>
            }
          >
            A secretaria vai analisar seus dados. Quando o cadastro for aprovado, você recebe por e-mail o
            convite para criar sua senha e acessar suas escalas.
          </Notice>
        ) : null}
        {phase.kind === 'ready' && phase.form.open ? (
          <Steps
            form={phase.form}
            today={today}
            onStep={toTop}
            onSubmit={async (data, photo) => {
              await source.submit(data, photo);
              setPhase({ kind: 'done', form: phase.form, firstName: data.name.trim().split(/\s+/)[0] ?? '' });
              toTop();
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

export function Notice({
  icon,
  tone,
  title,
  actions,
  children,
}: {
  icon: ReactNode;
  tone: 'success' | 'warning';
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card role="status" className="flex flex-col items-center gap-2.5 rounded-dialog px-7 py-10 text-center">
      <span
        aria-hidden
        className={cn(
          'grid size-13 place-items-center rounded-block',
          tone === 'success'
            ? 'rounded-pill bg-success-soft text-success'
            : 'bg-warning-soft text-warning-deep'
        )}
      >
        {icon}
      </span>
      <h1 className="m-0 text-display font-extrabold tracking-[-0.02em]">{title}</h1>
      <p className="m-0 max-w-110 text-pretty text-ink-muted">{children}</p>
      {actions ? <div className="mt-1.5 flex flex-wrap justify-center gap-2">{actions}</div> : null}
    </Card>
  );
}

/** As 4 etapas: cada "Continuar" confere a etapa; "Enviar cadastro" confere tudo e a LGPD. */
function Steps({
  form,
  today,
  onStep,
  onSubmit,
}: {
  form: RegistrationForm;
  today: string;
  /** Leva ao topo a cada troca de etapa. */
  onStep: () => void;
  onSubmit: (data: RegistrationData, photo: PhotoUpload | null) => Promise<void>;
}) {
  const [step, setStep] = useState<RegistrationStep>(0);
  const [data, setData] = useState<RegistrationData>(emptyRegistrationData);
  const [photo, setPhoto] = useState<PhotoUpload | null>(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const photoUrl = photo ? photoDataUrl(photo.type, photo.data) : null;
  const [errors, setErrors] = useState<RegistrationErrors>({});
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (patch: Partial<RegistrationData>) => {
    setData((d) => ({ ...d, ...patch }));
    setErrors((e) => {
      const next = { ...e };

      for (const key of Object.keys(patch)) delete next[key as keyof RegistrationErrors];
      if ('phone' in patch || 'email' in patch) {
        delete next.phone;
        delete next.email;
      }
      if ('address' in patch) {
        delete next.cep;
        delete next.uf;
      }

      return next;
    });
  };

  const go = (next: RegistrationStep) => {
    setStep(next);
    onStep();
  };

  const firstStepWithError = (e: RegistrationErrors): RegistrationStep => {
    if (e.name || e.birthDate || e.sex || e.phone || e.email) return 0;
    if (e.cep || e.uf) return 1;

    return 2;
  };

  const next = async () => {
    const found = registrationStepErrors(step, data, today);

    setErrors(found);

    if (Object.keys(found).length > 0) {
      if (step === 3) go(firstStepWithError(found));

      return;
    }

    if (step < 3) {
      go((step + 1) as RegistrationStep);

      return;
    }

    if (!consent) {
      setConsentError(true);

      return;
    }

    setBusy(true);
    setSendError(null);

    try {
      await onSubmit(normalizeRegistration(data), photo);
    } catch (error) {
      setSendError(authErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const ministries = form.ministries;

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 text-display-lg leading-tight font-extrabold tracking-[-0.03em]">
          Cadastro de membro
        </h1>
        <p className="m-0 text-ink-muted">
          Preencha seus dados para fazer parte da {form.org.name}. Leva cerca de 3 minutos.
        </p>
      </div>

      <ol aria-label="Etapas" className="m-0 flex list-none items-center gap-2 p-0">
        {REGISTRATION_STEPS.map((label, i) => {
          const done = i < step;
          const current = i === step;

          return (
            <li
              key={label}
              aria-current={current ? 'step' : undefined}
              className="flex min-w-0 flex-1 items-center gap-2"
            >
              <span
                className={cn(
                  'grid size-7 shrink-0 place-items-center rounded-pill border-[1.5px] text-meta font-bold',
                  done && 'border-primary bg-primary text-white',
                  current && 'border-primary bg-primary-soft text-primary',
                  !done && !current && 'border-line-input bg-white text-ink-subtle'
                )}
              >
                {done ? <Check aria-hidden className="size-3.5 stroke-3" /> : i + 1}
              </span>
              <span
                className={cn(
                  'truncate text-meta font-semibold max-sm:sr-only',
                  current ? 'text-ink' : 'text-ink-subtle'
                )}
              >
                {label}
              </span>
              {i < REGISTRATION_STEPS.length - 1 ? (
                <span
                  aria-hidden
                  className={cn('h-0.5 min-w-2 flex-1 rounded-pill', done ? 'bg-primary' : 'bg-line-card')}
                />
              ) : null}
            </li>
          );
        })}
      </ol>

      <Card className="flex flex-col gap-4.5 rounded-dialog p-6">
        <p className="m-0 text-meta font-semibold text-ink-subtle">
          Etapa {step + 1} de 4 · {REGISTRATION_STEPS[step]}
        </p>

        {step === 0 ? (
          <div className={GRID}>
            <PhotoField
              name={data.name}
              photo={photoUrl}
              onPick={() => setPhotoOpen(true)}
              onRemove={() => setPhoto(null)}
            />
            <FormField id="reg-name" label="Nome completo" error={errors.name} className="col-span-full">
              <Input
                id="reg-name"
                autoComplete="name"
                placeholder="Como no documento"
                value={data.name}
                onChange={(e) => set({ name: e.target.value })}
                {...fieldErrorProps('reg-name', errors.name)}
              />
            </FormField>
            <FormField id="reg-birth" label="Data de nascimento" error={errors.birthDate}>
              <DateField
                id="reg-birth"
                autoComplete="bday"
                max={today}
                value={data.birthDate ?? ''}
                onChange={(iso) => set({ birthDate: iso || null })}
                {...fieldErrorProps('reg-birth', errors.birthDate)}
              />
            </FormField>
            <div className="flex flex-col gap-1.5">
              <span className="text-small font-semibold">Sexo</span>
              <SegmentedControl
                aria-label="Sexo"
                block
                value={data.sex ?? ''}
                onValueChange={(sex) => set({ sex: sex === 'M' ? 'M' : 'F' })}
                options={[
                  {
                    value: 'F',
                    label: (
                      <>
                        <Venus aria-hidden />
                        Feminino
                      </>
                    ),
                  },
                  {
                    value: 'M',
                    label: (
                      <>
                        <Mars aria-hidden />
                        Masculino
                      </>
                    ),
                  },
                ]}
              />
              {errors.sex ? (
                <span role="alert" className="text-caption text-status-conflict">
                  {errors.sex}
                </span>
              ) : null}
            </div>
            <FormField id="reg-civil" label="Estado civil" optional>
              <SimpleSelect
                id="reg-civil"
                aria-label="Estado civil"
                value={data.civilStatus ?? 'none'}
                onValueChange={(v) => set({ civilStatus: v === 'none' ? null : (v as CivilStatus) })}
                options={CIVIL_OPTIONS}
              />
            </FormField>
            <FormField id="reg-job" label="Profissão" optional>
              <Input
                id="reg-job"
                placeholder="Ex.: Professora"
                value={data.profession}
                onChange={(e) => set({ profession: e.target.value })}
              />
            </FormField>
            <FormField id="reg-phone" label="Celular (WhatsApp)" error={errors.phone}>
              <Input
                id="reg-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                data-numeric
                placeholder="(11) 91234-5678"
                value={formatPhone(data.phone)}
                onChange={(e) => set({ phone: phoneDigits(e.target.value) })}
                {...fieldErrorProps('reg-phone', errors.phone)}
              />
            </FormField>
            <FormField id="reg-email" label="E-mail" error={errors.email}>
              <Input
                id="reg-email"
                type="email"
                autoComplete="email"
                placeholder="nome@exemplo.com"
                value={data.email}
                onChange={(e) => set({ email: e.target.value })}
                {...fieldErrorProps('reg-email', errors.email)}
              />
            </FormField>
            <p className="col-span-full -mt-1.5 mb-0 text-caption text-ink-subtle">
              Informe ao menos um contato. O e-mail é usado para enviar o convite de acesso ao app.
            </p>
          </div>
        ) : null}

        {step === 1 ? (
          <div className={GRID}>
            <AddressFields
              idPrefix="reg-address"
              address={data.address}
              optional
              errors={errors}
              onChange={(address) => set({ address })}
            />
          </div>
        ) : null}

        {step === 2 ? (
          <div className="flex flex-col gap-4.5">
            <div role="radiogroup" aria-label="Sua situação" className="flex flex-col gap-2">
              <span className="text-small font-semibold">Sua situação</span>
              {CHURCH_STATUS_ORDER.map((status) => {
                const selected = data.churchStatus === status;

                return (
                  <button
                    key={status}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-labelledby={`reg-status-${status}`}
                    aria-describedby={`reg-status-${status}-hint`}
                    onClick={() => set({ churchStatus: status })}
                    className={cn(
                      'flex min-h-12 items-center gap-3 rounded-block border-[1.5px] px-3.5 py-2.5 text-left',
                      selected
                        ? 'border-primary bg-primary-faint'
                        : 'border-line-input bg-white hover:bg-surface-subtle'
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'grid size-4.5 shrink-0 place-items-center rounded-pill border-2',
                        selected ? 'border-primary' : 'border-ink-placeholder'
                      )}
                    >
                      {selected ? <span className="size-2 rounded-pill bg-primary" /> : null}
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span id={`reg-status-${status}`} className="text-body font-medium">
                        {CHURCH_STATUS_LABEL[status]}
                      </span>
                      <span id={`reg-status-${status}-hint`} className="text-caption text-ink-subtle">
                        {CHURCH_STATUS_HINT[status]}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className={GRID}>
              <ChurchFields
                idPrefix="reg-church"
                value={data}
                today={today}
                optional
                withStatus={false}
                errors={errors}
                onChange={set}
              />
            </div>
            {ministries.length ? (
              <div className="flex flex-col gap-2">
                <span className="text-small font-semibold">
                  Ministérios em que gostaria de servir{' '}
                  <span className="font-normal text-ink-subtle">(opcional)</span>
                </span>
                <div className="flex flex-wrap gap-2">
                  {ministries.map((m) => {
                    const selected = data.ministryIds.includes(m.id);

                    return (
                      <button
                        key={m.id}
                        type="button"
                        aria-pressed={selected}
                        onClick={() =>
                          set({
                            ministryIds: selected
                              ? data.ministryIds.filter((id) => id !== m.id)
                              : [...data.ministryIds, m.id],
                          })
                        }
                        className={cn(
                          'inline-flex h-9.5 items-center gap-1.5 rounded-pill border-[1.5px] px-3.5 text-control',
                          selected
                            ? 'border-primary bg-primary-faint font-semibold'
                            : 'border-line-input bg-white font-medium hover:bg-surface-subtle'
                        )}
                      >
                        <span className="inline-flex" style={{ color: m.color }}>
                          <MinistryIcon icon={m.icon} className="size-4" />
                        </span>
                        {m.name}
                      </button>
                    );
                  })}
                </div>
                <span className="text-caption text-ink-subtle">
                  Os líderes confirmam a participação depois da aprovação do cadastro.
                </span>
              </div>
            ) : null}
            <FormField id="reg-notes" label="Algo que queira contar?" optional error={errors.notes}>
              <textarea
                id="reg-notes"
                rows={3}
                maxLength={1000}
                placeholder="Ex.: dons, experiência em algum ministério..."
                value={data.notes}
                onChange={(e) => set({ notes: e.target.value })}
                className="resize-y rounded-block border border-line-input bg-white px-3.5 py-2.5 text-body"
              />
            </FormField>
          </div>
        ) : null}

        {step === 3 ? (
          <Review
            data={data}
            photo={photoUrl}
            form={form}
            onEdit={go}
            consent={consent}
            consentError={consentError}
            onConsent={(v) => {
              setConsent(v);
              if (v) setConsentError(false);
            }}
          />
        ) : null}

        {sendError ? <Alert tone="danger">{sendError}</Alert> : null}

        <div className="flex items-center gap-2 border-t border-line-divider pt-4">
          {step > 0 ? (
            <Button variant="secondary" size="lg" onClick={() => go((step - 1) as RegistrationStep)}>
              Voltar
            </Button>
          ) : null}
          <span className="flex-1" />
          <Button size="lg" className="font-bold shadow-cta" disabled={busy} onClick={() => void next()}>
            {step === 3 ? 'Enviar cadastro' : 'Continuar'}
            <ArrowRight aria-hidden />
          </Button>
        </div>
      </Card>
      <p className="m-0 text-center text-caption text-ink-subtle">
        Seus dados ficam visíveis apenas para a secretaria e os líderes dos ministérios.
      </p>

      {photoOpen ? (
        <PhotoDialog
          name={data.name}
          photo={photoUrl}
          hint="Uma foto do rosto ajuda a secretaria a reconhecer você."
          onSave={(upload) => {
            setPhoto(upload);

            return true;
          }}
          onRemove={() => {
            setPhoto(null);

            return true;
          }}
          onClose={() => setPhotoOpen(false)}
        />
      ) : null}
    </>
  );
}

/** A foto opcional, no topo de "Seus dados": só é enviada com o cadastro. */
function PhotoField({
  name,
  photo,
  onPick,
  onRemove,
}: {
  name: string;
  photo: string | null;
  onPick: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="col-span-full flex items-center gap-4">
      <Avatar name={name.trim() || 'Novo membro'} photo={photo} size="3xl" />
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="text-small font-semibold">
          Sua foto <span className="font-normal text-ink-subtle">(opcional)</span>
        </span>
        <span className="text-caption text-pretty text-ink-subtle">
          Ajuda a secretaria a reconhecer você na aprovação do cadastro.
        </span>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={onPick}>
            <Camera aria-hidden />
            {photo ? 'Trocar foto' : 'Adicionar foto'}
          </Button>
          {photo ? (
            <Button variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 aria-hidden />
              Remover foto
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Revisão: cada grupo com "Editar" e a autorização da LGPD, obrigatória para enviar. */
function Review({
  data,
  photo,
  form,
  consent,
  consentError,
  onConsent,
  onEdit,
}: {
  data: RegistrationData;
  photo: string | null;
  form: RegistrationForm;
  consent: boolean;
  consentError: boolean;
  onConsent: (value: boolean) => void;
  onEdit: (step: RegistrationStep) => void;
}) {
  const dash = (value: string | null | undefined) => (value ? value : '—');
  const groups: { title: string; step: RegistrationStep; rows: [string, string][] }[] = [
    {
      title: 'Seus dados',
      step: 0,
      rows: [
        ['Nome', dash(data.name)],
        ['Nascimento', data.birthDate ? formatNumericDate(data.birthDate) : '—'],
        ['Sexo', data.sex ? SEX_LABEL[data.sex] : '—'],
        ['Estado civil', data.civilStatus ? CIVIL_STATUS_LABEL[data.civilStatus] : '—'],
        ['Profissão', dash(data.profession)],
        ['Celular', dash(formatPhone(data.phone))],
        ['E-mail', dash(data.email)],
      ],
    },
    {
      title: 'Endereço',
      step: 1,
      rows: [['Endereço', dash(addressLine(data.address))]],
    },
    {
      title: 'Vida na igreja',
      step: 2,
      rows: [
        ['Situação', data.churchStatus ? CHURCH_STATUS_LABEL[data.churchStatus] : '—'],
        ['Membro desde', data.memberSince ? formatNumericDate(data.memberSince) : '—'],
        ['Forma de ingresso', data.joinedVia ? JOINED_VIA_LABEL[data.joinedVia] : '—'],
        ['Batismo', data.baptismDate ? formatNumericDate(data.baptismDate) : '—'],
        ...(showsOriginChurch(data.joinedVia)
          ? ([['Igreja de origem', dash(data.originChurch)]] as [string, string][])
          : []),
        [
          'Ministérios',
          dash(
            form.ministries
              .filter((m) => data.ministryIds.includes(m.id))
              .map((m) => m.name)
              .join(', ')
          ),
        ],
        ['Observações', dash(data.notes)],
      ],
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {groups.map((g) => (
        <div key={g.title} className="overflow-hidden rounded-card border border-line-card">
          <div className="flex items-center border-b border-line-divider bg-surface-subtle px-3.5 py-2.5">
            <h2 className="m-0 flex-1 text-control font-bold">{g.title}</h2>
            <Button
              variant="link"
              size="sm"
              aria-label={`Editar ${g.title.toLowerCase()}`}
              onClick={() => onEdit(g.step)}
            >
              <Pencil aria-hidden />
              Editar
            </Button>
          </div>
          {g.step === 0 ? (
            <div className="flex items-center gap-2.5 px-3.5 pt-2.5">
              <Avatar name={data.name.trim() || 'Novo membro'} photo={photo} size="2xl" />
              <span className="text-control text-ink-secondary">{photo ? 'Com foto' : 'Sem foto'}</span>
            </div>
          ) : null}
          <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(min(100%,190px),1fr))] gap-x-4 gap-y-2 px-3.5 py-2.5">
            {g.rows.map(([k, v]) => (
              <div key={k} className="min-w-0">
                <dt className="text-caption font-semibold text-ink-subtle">{k}</dt>
                <dd className="m-0 text-control wrap-anywhere">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
      <button
        type="button"
        role="checkbox"
        aria-checked={consent}
        aria-describedby={consentError ? 'reg-consent-error' : undefined}
        onClick={() => onConsent(!consent)}
        className={cn(
          'flex items-start gap-2.5 rounded-block border bg-white px-3.5 py-3 text-left',
          consentError ? 'border-status-conflict' : 'border-line-input'
        )}
      >
        <CheckboxMark checked={consent} className="mt-0.5" />
        <span className="text-small text-pretty text-ink-secondary">{CONSENT_TEXT}</span>
      </button>
      <p className="m-0 -mt-1 text-meta text-ink-subtle">
        Leia a{' '}
        <LegalLink doc="privacy" orgName={form.org.name}>
          Política de Privacidade
        </LegalLink>{' '}
        e os{' '}
        <LegalLink doc="terms" orgName={form.org.name}>
          Termos de Uso
        </LegalLink>
        .
      </p>
      {consentError ? (
        <span id="reg-consent-error" role="alert" className="text-caption text-status-conflict">
          Para enviar, autorize o uso dos dados.
        </span>
      ) : null}
    </div>
  );
}
